import { type Row } from "@tanstack/react-table";

import { type SpatialAnalysisRow } from "@/components/spatialAnalysis/spatialAnalysisTable/columns";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { Timezone } from "@/constants/dateTime";
import { type PreviewData } from "@/hooks/map/usePreviewGeom";

import {
  CountryCode,
  Pagination,
  Source,
  SpatialAggregationType
} from "./common";
import { GridConfigResponse } from "./project";
import { EOutputAnalysisType, ScoringOptions } from "./spatial";

export interface FilterOption {
  id: string;
  label: string;
  value?: string;
  type?: EFilterOptionType | null;
  data_type?: string;
}

export type AttributeOption = IOptionItem & {
  id: string;
  attribute_code?: string;
  type?: EFilterOptionType | null;
  data_type?: string;
  time_format?: string | null;
  value?: string;
  paired_key_attribute_id: string | null;
  group_by_attr?: boolean;
};

export enum EAttributeType {
  ADDRESS = "address",
  TITLE = "title",
  TEXT = "text",
  STRING = "string",
  BOOLEAN = "bool",
  INT = "int",
  FLOAT = "float",
  DATE_TIME = "datetime",
  DATE = "date",
  TIME = "time"
}

export enum EFilterOptionType {
  MULTIPLE = "multiple",
  MIN_MAX = "minmax",
  DATE_TIME = "datetime",
  DATE = "date",
  TIME = "time"
}

export enum ExportDatasetFileType {
  CSV = "csv",
  XLSX = "xlsx",
  GEOJSON = "geojson",
  SHAPEFILE = "shapefile"
}

export enum DownloadOnlyFileType {
  PDF = "pdf"
}

export type DownloadFileType = ExportDatasetFileType | DownloadOnlyFileType;

export type DATASET_SORT_TYPE =
  | "name"
  | "country"
  | "spatial_aggregation_type"
  | "status"
  | "date"
  | "updated_at"
  | "size";

export enum DatasetPublishStatus {
  PUBLISHED = "Published",
  UNPUBLISHED = "Unpublished"
}

export enum ATTRIBUTE_ID {
  id = "id",
  grid_id = "grid_id",
  catchment_id = "catchment_id"
}

export enum DatasetDataType {
  TYPE_STRING = "TYPE_STRING",
  TYPE_INTEGER = "TYPE_INTEGER",
  TYPE_FLOAT = "TYPE_FLOAT",
  TYPE_BOOLEAN = "TYPE_BOOLEAN",
  TYPE_DATETIME = "TYPE_DATETIME",
  TYPE_JSON = "TYPE_JSON", // For nested/complex data
  TYPE_GEOMETRY = "TYPE_GEOMETRY", // For spatial data
  TYPE_DATE = "TYPE_DATE",
  TYPE_TIME = "TYPE_TIME",
  TYPE_ADDRESS = "TYPE_ADDRESS",
  TYPE_TITLE = "TYPE_TITLE",
  TYPE_PHOTOS = "TYPE_PHOTOS",
  TYPE_LINKS = "TYPE_LINKS",
  TYPE_LATITUDE = "TYPE_LATITUDE",
  TYPE_LONGITUDE = "TYPE_LONGITUDE",
  TYPE_PERCENTAGE = "TYPE_PERCENTAGE",
  TYPE_RATIO = "TYPE_RATIO",
  TYPE_ID = "TYPE_ID"
}

export enum DataSchemaType {
  ID = "id",
  TEXT2 = "text", // TOOD: Temporary until BE fix the data type
  TEXT = "string",
  INT = "int",
  FLOAT = "float",
  BOOL = "bool",
  JSON = "json",
  GEOMETRY = "geometry",
  DATE = "date",
  TIME = "time",
  DATE_TIME = "datetime",
  ADDRESS = "address",
  PHOTOS = "photos",
  LINKS = "links",
  TITLE = "title",
  LATITUDE = "latitude",
  LONGITUDE = "longitude",
  PERCENTAGE = "percentage",
  RATIO = "ratio"
}

export enum DatasetSchemaLabel {
  POLYGON = "polygon",
  POINT = "point",
  H3 = "H3",
  GEOHASH = "geohash",
  LINE = "line",
  TITLE = "title",
  ID = "id",
  ADDRESS = "address",
  LONGITUDE = "longitude",
  LATITUDE = "latitude",
  RATIO = "ratio",
  PERCENTAGE = "percentage"
}

export enum OperationStatusType {
  STARTED = "STARTED",
  PROCESSING = "PROCESSING",
  IN_PROGRESS = "IN_PROGRESS",
  STARTING = "STARTING",
  IMPORTING = "IMPORTING",
  UPDATING = "UPDATING",
  EXPORTING = "EXPORTING",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED"
}

export enum OperationType {
  EXPORT = "EXPORT",
  IMPORT = "IMPORT",
  IMPORT_ADD = "IMPORT_ADD",
  IMPORT_NEW = "IMPORT_NEW",
  IMPORT_REPLACE = "IMPORT_REPLACE"
}

export interface DatasetOperationStreamCompletedResponse {
  dataset_id: string;
  final_seq: number;
  total_rows: number;
}

export interface OperationStatus {
  id: string;
  dataset_id: string;
  export_url?: string;
  operation_type: OperationType;
  status: OperationStatusType;
  message?: string;
  import_count: {
    duplicate: number;
    failed: number;
    success: number;
    total: number;
  };
}

export type WeightValueEntry = {
  weight: string;
  percentage?: string;
  negative_impact?: boolean;
  include_scoring?: boolean;
};

export type WeightValues = {
  [datasetId: string]: {
    weight: string;
    include_scoring?: boolean;
    attributes: Record<string, WeightValueEntry>;
  };
};

export type DatasetsMap = {
  [id: string]: Dataset;
};

export type ColorRanges = {
  min?: number | null;
  max?: number | null;
  value?: string | null;
  color: string;
  label: string;
};

export type LabelConfig = {
  code?: string;
  attribute_id: string;
  attribute_name: string;
  show_label: boolean;
  color_scale: EColorLabel;
  opacity: number;
};

export type RankConfig = {
  isEnabled: boolean;
  maxRanking: number;
};

export type StrokeConfig = {
  code?: string;
  attribute_id?: string;
  attribute_name?: string;
  width: number;
  opacity: number;
};

export interface DatasetStyle {
  // For grid-specific color ranges
  colorRanges: ColorRanges[];
  color: string;
  weight: number;
  opacity: number;
  fillColor: string;
  fillOpacity: number;
  visibility: "visible" | "none";
  colorFamily: EColorNames;
  labelConfig: LabelConfig;
  colorSteps: number;
  rankConfig?: RankConfig;
  stroke: StrokeConfig;
}

export interface DatasetExploreItem {
  id: string;
  name: string;
  display_name?: string;
  bbox?: string[];
  country_id: CountryCode;
  source: Source;
  spatial_aggregation_type: SpatialAggregationType;
  short_description: string;
  published: boolean;
  created_at: string;
  updated_at: string;
  description?: string;
  available?: boolean;
  total_rows: number;
  adm_area_level?: number | null;
  grid_level?: number | null;
  license?: Omit<License, "all">;
  is_subscribed?: boolean;
  provider?: {
    id: string;
    logo_asset_url: string;
    name: string;
  };
  min_area_level_filter?: number;
  dataset_size_kb?: number;
  size_kb?: number;
  timeseries?: boolean;
}

export interface DatasetMetadata extends DatasetExploreItem {
  schema: DatasetAttributeSchema[];
}
export interface DatasetSchemas extends DatasetExploreItem {
  schemas: DatasetAttributeSchema[];
}
// FIXME: Merge `DatasetAttributeSchema` type with `DatasetDetailSchemaResponse` type as they share the same structure
export interface DatasetAttributeSchema {
  code?: string;
  id: string;
  attribute_name: string;
  attribute_code?: string;
  data_type: DataSchemaType;
  data_label?: DatasetSchemaLabel; // will updated to enum after all related ticket done
  is_nullable: boolean;
  is_reference_geo: boolean;
  is_reference_id: boolean;
  is_spatial: boolean;
  position: number;
  created_at: string;
  updated_at: string;
  time_format?: string | null;
  is_visible: boolean;
  paired_key_attribute_id: string | null;
  group_by_attr?: boolean;
  attribute_column?: string;
}

export interface DatasetProperties {
  isVisible: boolean;
  spatialType: SpatialAggregationType;
  isFetching?: boolean;
}

export interface H3Configuration {
  resolution?: number;
  aggregationType?: string;
}

export interface Dataset {
  data: DatasetDetailResponse;
  properties: DatasetProperties;
  style: DatasetStyle;
  // TODO: Change to attributes
  categories: DatasetAttributeSchema[];
  selectedCategory: DatasetAttributeSchema;
  h3Configuration?: H3Configuration | null;
  group_by?: string;
  is_global_admin_area?: boolean;
}

export interface DatasetFilter {
  filterId: string;
  filterLabel: string;
  filterType: EFilterOptionType;
  selectedOptions?: string[];
  /** Options that were selected but no longer exist in the filtered attribute values */
  invalidOptions?: string[];
  time_format?: string | null;
  dateTimeRange?: {
    from: {
      date: string;
      time: string;
    };
    to: {
      date: string;
      time: string;
    };
    includeNulls: boolean;
  };
  dateRange?: {
    from: string;
    to: string;
    includeNulls: boolean;
  };
  timeRange?: {
    from: string;
    to: string;
    includeNulls: boolean;
  };
  minMaxRange?: {
    min: string;
    max: string;
    includeNulls: boolean;
  };
  attributeCode?: string;
  attributeId: string;
  paired_key_attribute_id?: string | null;
  dataset_id?: string;
  is_multiple_adm_area?: boolean;
  data_type?: string;
}

export interface DatasetFilterPayload {
  id: string;
  filter?: MultipleSelectFilter | DateFilter | MinMaxFilter;
  attribute_code?: string; // Optional, used for group_name_code
}

export interface DatasetVisualization {
  id: string;
  datasetId: string;
  type: "choropleth" | "heatmap" | "category" | "bubble";
  property: string;
  colorScheme?: string;
  breaks?: number[];
  classification?: "equal-interval" | "quantile" | "natural-breaks";
  opacity?: number;
}

export interface GetDatasetListPayload {
  q: string;
  country?: CountryCode;
  source: Source;
  spatial_aggregation_type: SpatialAggregationType | "all";
  sort_by?: DATASET_SORT_TYPE;
  sort?: "ASC" | "DESC";
  published?: boolean;
  page: number;
  per_page: number;
  license?: Omit<License, "all"> | "regular,premium";
  provider_id?: string;
}

export interface GetDatasetListResponse {
  data: DatasetExploreItem[];
  meta: {
    pagination: Pagination;
  };
}

export interface DatasetDataColumn {
  schema_id?: string;
  code: string;
  name: string;
  data_type: DatasetDataType;
  data_label?: DatasetSchemaLabel;
  is_visible: boolean;
  is_reference_geo: boolean;
  time_format?: string | null;
  is_measure?: boolean;
  is_dimension?: boolean;
}

export type DatasetDataRowValue = string | number | boolean | null | undefined;

export type DatasetDataRowV2 = DatasetDataRowValue[];

export interface DatasetData {
  columns: DatasetDataColumn[];
  rows: DatasetDataRowV2[];
  dataset_id: string;
  dataset_size_kb: number;
  name: string;
  display_name?: string;
  spatial_aggregation_type: SpatialAggregationType;
  timeseries?: boolean;
}

export interface DatasetMetadataStream {
  dataset_id: string;
  name: string;
  total_rows: number;
  dataset_size_kb: number;
  max_rows_per_chunk: number;
  spatial_aggregation_type: SpatialAggregationType;
  columns: DatasetDataColumn[];
}

export interface DatasetChunkStream {
  dataset_id: string;
  rows: DatasetDataRowV2[];
  seq: number;
}

export interface DatasetStreamDone {
  dataset_id: string;
  final_seq: number;
  total_rows: number;
}

export interface DatasetDetailSchemaResponse {
  code?: string;
  id: string;
  attribute_name: string;
  data_type: string; // TODO update to DataSchemaType
  is_nullable: boolean;
  is_spatial: boolean;
  position: number;
  created_at: string;
  updated_at: string;
  is_reference_geo?: boolean;
  is_reference_id?: boolean;
  attribute_code?: string;
  time_format?: string | null;
  data_label?: string;
  is_visible: boolean;
  paired_key_attribute_id: string | null;
  group_by_attr?: boolean;
  attribute_column?: string;
  /** Only used in dataset detail response to indicate whether the attribute is dimension or measure. */
  is_dimension?: boolean;
  /** Only used in dataset detail response to indicate whether the attribute is dimension or measure. */
  is_measure?: boolean;
  /** Only used in dataset detail response to indicate the breakdown group of the attribute. */
  breakdown_group_id?: string;
  /** Only used in dataset detail response to indicate the breakdown group name of the attribute. */
  breakdown_group_label?: string;
}

export interface DatasetDetailResponse extends DatasetExploreItem {
  schema: DatasetDetailSchemaResponse[];
}

// Below is the filter types get from yml file
export type MultipleSelectFilter = string[];

export interface MinMaxFilter {
  min: number | null;
  max: number | null;
  include_nulls: boolean;
}

export interface DateFilter {
  start: string | null; // ISO date format
  end: string | null; // ISO date format
  include_nulls: boolean;
}

export interface SpatialAnalysisDatasetFilter {
  schema_id: string;
  filter: MultipleSelectFilter | DateFilter | MinMaxFilter;
  attribute_code: string;
}

export interface DatasetWeightWithSchemaId {
  schema_id: string;
  weight: number;
  negative_impact?: boolean;
  include_scoring?: boolean;
}

export type DatasetSchemaPayload = Omit<
  DatasetAttributeSchema,
  "created_at" | "updated_at"
>;

export interface DatasetWeight {
  weight: number;
  negative_impact?: boolean;
}

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export interface Marker {
  name: string;
  coordinate: Coordinate;
  polygon: string[];
}

export interface AdminArea {
  id?: string;
  adm_area_ids?: string[];
}

export interface CustomCatchment {
  name: string;
  polygon: string[];
}
export interface GridConfig {
  grid_type: AnalysisResultType.TYPE_GEOHASH | AnalysisResultType.TYPE_H3;
  level: number;
}

export interface OutputSiteProfiling {
  output_type: AnalysisResultType.TYPE_SITE_PROFILING;
  scoring_option: ScoringOptions;
}

export interface OutputGrid {
  output_type: AnalysisResultType.TYPE_GRID;
  grid_config: GridConfig;
  scoring_option: ScoringOptions;
}

export enum AnalysisResultType {
  TYPE_SITE_PROFILING = "TYPE_SITE_PROFILING",
  TYPE_GRID = "TYPE_GRID",
  TYPE_GEOHASH = "TYPE_GEOHASH",
  TYPE_H3 = "TYPE_H3"
}

export interface SpatialAnalysisDataset {
  id: string;
  filter?: SpatialAnalysisDatasetFilter[];
  weight: (DatasetWeightWithSchemaId | DatasetWeight)[];
  display_name?: string;
  grid_config?: GridConfigResponse;
  group_by?: string;
  include_scoring?: boolean;
}

export interface DatasetAnalysisPayload {
  datasets: SpatialAnalysisDataset[];
  input: Marker[] | AdminArea | CustomCatchment[];
  output: OutputSiteProfiling | OutputGrid;
  configuration?: string;
}

export interface DatasetMetadataQueryParams {
  id: string;
  h3Configuration?: H3Configuration | null;
  show_id?: boolean;
  group_by?: string;
}

// Value types in dataset response rows
export interface StringValue {
  string_value: string;
}
export interface IntValue {
  int_value: number;
}
export interface FloatValue {
  FloatValue: number;
}
export interface BoolValue {
  BoolValue: boolean;
}
export interface JsonValue {
  JsonValue: string;
}
export interface TimestampValue {
  TimestampValue: string;
}
export interface NullValue {
  NullValue: string;
}

export type ValueType =
  | StringValue
  | IntValue
  | FloatValue
  | BoolValue
  | JsonValue
  | TimestampValue
  | NullValue;

// Row in dataset response
export interface AnalysisRow {
  values: ValueType[];
}

// Error message in analysis response
export interface ErrorMessage {
  message: string;
}

// Analysis response status
export enum AnalysisStatus {
  PENDING = "PENDING",
  STARTED = "STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED"
}

// Analysis response
export interface AnalysisResponse {
  id: string;
  status: AnalysisStatus;
  result: DatasetData[];
}

// Error response
export interface ErrorResponse {
  error: {
    code: string;
    message: string;
  };
}

export interface DatasetMetaDataPayload {
  id: string;
  country_id?: string;
  spatial_aggregation_type?: string;
  published?: boolean;
  name?: string;
  short_description?: string;
}

export interface DatasetAttributeValue {
  display_name: string;
  value: string;
}

export interface DatasetBasicInfoForm {
  name?: string;
  short_description?: string;
}

export interface TranslateDatasetGeometryPayload {
  country_id: string;
  spatial_aggregation_type: Omit<
    SpatialAggregationType,
    EOutputAnalysisType.GRID | EOutputAnalysisType.PROFILING
  >;
  source_attribute_ids: string[];
  geocoding?: boolean;
}

export interface TranslateDatasetGeometryResponse {
  failed: number;
  successful: number;
  total: number;
}

export interface TranslateDatasetGeometryErrorResponse {
  error: {
    code: string;
    details: [
      { field: "available_kb"; message: string },
      { field: "required_kb"; message: string }
    ];
    message: string;
  };
}

export interface SaveAsDatasetErrorDetail {
  field: string;
  message: string;
}

export interface TransformedProperty {
  key: string;
  value: string | number | boolean | null;
  data_type: DataSchemaType;
  data_label?: DatasetSchemaLabel | null;
}

export interface SpatialAggregationTypeItem {
  code: SpatialAggregationType;
  name: string;
}

export interface StyleConfig {
  colorFamily?: EColorNames;
  opacity?: number;
  selectedCategory?: DatasetAttributeSchema;
  labelConfig?: LabelConfig;
  colorSteps?: number;
  rankConfig?: RankConfig;
  strokeConfig?: StrokeConfig;
}

export type CreateNewDatasetType = {
  dataset: DatasetDetailResponse;
  datasetId: string;
  datasetName: string;
  attributesSchema: DatasetAttributeSchema[];
  h3Configuration?: H3Configuration | null;
  styleConfig?: StyleConfig;
  totalRows?: number;
  index?: number;
  group_by?: string;
  is_global_admin_area?: boolean;
};

export type License = "all" | "regular" | "premium";

export interface DataProviderItem {
  id: string;
  logo_asset_url: string;
  name: string;
}

export enum EColorLabel {
  BLACK_WHITE = "BLACK_WHITE",
  WHITE_BLACK = "WHITE_BLACK"
}

export enum EColorNames {
  TEAL = "TEAL",
  GREEN = "GREEN",
  VIOLET = "VIOLET",
  BLUE = "BLUE",
  MARINE = "MARINE",
  TURQUOISE = "TURQUOISE",
  ORANGE = "ORANGE",
  RED = "RED",
  PINK = "PINK",
  PURPLE = "PURPLE",
  GRAY = "GRAY",
  CYBER_SUNSET = "CYBER_SUNSET",
  SPRING_MEADOW = "SPRING_MEADOW",
  DESERT_OASIS = "DESERT_OASIS",
  WATERMELON = "WATERMELON",
  HEAT_WAVE = "HEAT_WAVE",
  NEON_CANDY = "NEON_CANDY",
  RETRO_ARCADE = "RETRO_ARCADE",
  TROPICAL_PUNCH = "TROPICAL_PUNCH",
  MOSAIC = "MOSAIC"
}

export type ColorMap = EColorNames | EColorLabel;

export interface DatasetHistory {
  datasets: DatasetsMap;
  visualizationOrder: string[];
  rawDatasets: {
    [id: string]: DatasetData;
  };
  filters?: {
    [id: string]: DatasetFilter[];
  };
}

export interface DatasetIndices {
  [baseId: string]: {
    counter: number;
    usedIndices: number[];
  };
}
export const SchemaToDatasetMap: Record<DataSchemaType, DatasetDataType> = {
  [DataSchemaType.ID]: DatasetDataType.TYPE_ID,
  [DataSchemaType.TEXT]: DatasetDataType.TYPE_STRING,
  [DataSchemaType.TEXT2]: DatasetDataType.TYPE_STRING,
  [DataSchemaType.INT]: DatasetDataType.TYPE_INTEGER,
  [DataSchemaType.FLOAT]: DatasetDataType.TYPE_FLOAT,
  [DataSchemaType.BOOL]: DatasetDataType.TYPE_BOOLEAN,
  [DataSchemaType.JSON]: DatasetDataType.TYPE_JSON,
  [DataSchemaType.GEOMETRY]: DatasetDataType.TYPE_GEOMETRY,
  [DataSchemaType.DATE]: DatasetDataType.TYPE_DATE,
  [DataSchemaType.TIME]: DatasetDataType.TYPE_TIME,
  [DataSchemaType.DATE_TIME]: DatasetDataType.TYPE_DATETIME,
  [DataSchemaType.ADDRESS]: DatasetDataType.TYPE_ADDRESS,
  [DataSchemaType.PHOTOS]: DatasetDataType.TYPE_PHOTOS,
  [DataSchemaType.LINKS]: DatasetDataType.TYPE_LINKS,
  [DataSchemaType.TITLE]: DatasetDataType.TYPE_TITLE,
  [DataSchemaType.LATITUDE]: DatasetDataType.TYPE_LATITUDE,
  [DataSchemaType.LONGITUDE]: DatasetDataType.TYPE_LONGITUDE,
  [DataSchemaType.PERCENTAGE]: DatasetDataType.TYPE_PERCENTAGE,
  [DataSchemaType.RATIO]: DatasetDataType.TYPE_RATIO
};

export interface DeleteRowsDatasetResponse {
  dataset_id: string;
  dataset_name: string;
  dataset_size_kb: number;
  deleted_rows: number;
  successful_deletes: string[];
  total_rows: number;
}

export interface AddUpdateRowsDatasetPayload {
  code: string;
  name: string;
  value: string;
}

export interface AddUpdateRowsDatasetResponse {
  code: string;
  columns: DatasetDataColumn[];
  rows: DatasetDataRowV2[];
  dataset_id: string;
  name: string;
  dataset_size_kb: number;
  spatial_aggregation_type: SpatialAggregationType;
}

export interface DatasetColumnConfig {
  timezone?: Timezone;
  isShowActionMapPreviewEnabled?: boolean;
  handlePreviewClick?: (
    value: string | null,
    type: "grid" | "wkt"
  ) => Promise<void>;
  getPreviewDataFromRow?: (row: Row<SpatialAnalysisRow>) => PreviewData;
}

export enum TimeseriesType {
  TIME = "time",
  DAY = "day",
  WEEK = "week",
  DATE = "date"
}

export type DatasetTimeseriesData = {
  time: string;
  value: number;
  name?: string;
  id?: string;
};
